import { Button, Divider, Link, Paper, Theme, Typography, useTheme } from '@mui/material';
//| import { Theme } from '@mui/material/styles';
import { makeStyles, createStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ClientSettings } from '../../components/ClientSettings';
import { ClientUsersTable } from '../../components/ClientUsersTable';
import { CustomSnackbar } from '../../components/CustomSnackbar';
import Search from '../../components/FiltersComponent/Search';
import { ArrowForwardOutlined } from '../../components/Icons/ArrowForwardOutlined';
import { Loading } from '../../components/Loading';
import { ScreenTitle } from '../../components/ScreenTitle';
import { DeleteDialog } from '../../components/dialogs/DeleteDialog';
import { PaymentMethodDialog } from '../../components/dialogs/PaymentMethodDialog';
import { fetchClients, toggleBilling } from '../../store/action_creators/clients.actions';
import { deletePaymentMethod, fetchPaymentMethods } from '../../store/action_creators/paymentMethods.actions';
import { Status } from '../../store/config/enums';
import { Client, PaymentMethod, RootState } from '../../store/config/types';
import colors from '../../styles/_variables.module.scss';
import PlexoCommerceText from './PlexoCommerceText';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: '2rem 0',
      maxWidth: '60rem',
      margin: '0 auto',
    },
    divider: {
      margin: '0 0.5rem',
      alignSelf: 'center',
      height: '1rem',
      width: '2px',
      backgroundColor: colors.slateBlue,
    },
    screenTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      width: '100%',
      marginBottom: '0.5rem',
    },
    button: {
      color: colors.slateBlue,
    },
    paperRoot: {
      margin: '1rem 0',
      padding: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      color: colors.slateBlue,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        '& svg': {
          '& rect': {
            fill: theme.palette.action.hover,
          },
        },
      },
    },
    clientNames: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
  }),
);

type PaymentMethodWithIsLast = PaymentMethod & { isLast: boolean };

function ManageClients() {
  const classes = useStyles(useTheme());
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const { clients, paymentMethods } = useSelector((state: RootState) => state);
  const client = location?.state?.client || null;
  const [selectedClient, setSelectedClient] = useState<Client | null>(client);
  const [open, setOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [deletingPaymentMethod, setDeletingPaymentMethod] = useState<boolean>(false);
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState<PaymentMethodWithIsLast | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  useEffect(() => {
    selectedClient?.id && dispatch(fetchPaymentMethods(+selectedClient?.id));
  }, [selectedClient?.id, dispatch]);

  useEffect(() => {
    if (
      paymentMethods.deletePaymentMethodSuccess ||
      paymentMethods.deletePaymentMethodErrorMessage !== null
    ) {
      setDeletingPaymentMethod(false);
      setDeleteOpen(false);
      setSnackbarOpen(true);
    }
  }, [paymentMethods.deletePaymentMethodSuccess, paymentMethods.deletePaymentMethodErrorMessage]);

  const showClientDetails = (client: Client) => {
    setSelectedClient(client);
  };

  const handleBillingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(toggleBilling({ id: selectedClient!.id, enabled: !selectedClient!.billingEnabled }));
  };

  const createPaymentMethod = () => {
    setSelectedPaymentMethod(null);
    setOpen(true);
  };

  const updatePaymentMethod = (paymentMethod: PaymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    setOpen(true);
  };

  const beginDeletePaymentMethod = (paymentMethod: PaymentMethod, isLast: boolean) => {
    setPaymentMethodToDelete({ ...paymentMethod, isLast });
    setDeleteOpen(true);
  };

  const confirmDeletePaymentMethod = () => {
    setDeletingPaymentMethod(true);

    if (paymentMethodToDelete && selectedClient?.id)
      dispatch(
        deletePaymentMethod(
          selectedClient?.id,
          paymentMethodToDelete?.issuerId,
          paymentMethodToDelete.isLast,
        ),
      );
  };

  const closeSnack = () => {
    setSnackbarOpen(false);
  };

  const triggerSearch = () => {
    if (searchTerm.length > 3 || searchTerm.length === 0) {
      dispatch(fetchClients(searchTerm));
    }
  };

  useEffect(() => {
    if (!clients.loadingClients && clients.clients === null && clients.loadingClientsErrorMessage === null) {
      dispatch(fetchClients(searchTerm));
    }
  });

  return (
    <div className={classes.container}>
      <div className={classes.screenTitle}>
        <ScreenTitle title={selectedClient === null ? 'Clientes' : selectedClient.prettyName}>
          {selectedClient && (
            <Link className={classes.button} onClick={() => setSelectedClient(null)}>
              <Button variant="outlined" color="secondary">
                Volver
              </Button>
            </Link>
          )}
        </ScreenTitle>
      </div>
      {selectedClient === null && (
        <>
          <Search
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            triggerSearch={triggerSearch}
            placeholder="Buscar por cliente"
          />
        </>
      )}

      {clients.loadingClients ? (
        <Loading />
      ) : (
        selectedClient === null &&
        clients.clients &&
        clients.clients.map((client) => {
          return (
            <Paper
              key={client.id}
              elevation={0}
              variant="outlined"
              classes={{ root: classes.paperRoot }}
              onClick={() => showClientDetails(client)}
            >
              <div className={classes.clientNames}>
                <Typography variant="h6">{client.prettyName}</Typography>
                <Divider orientation="vertical" className={classes.divider} />
                {client.plexoCommerceName}
              </div>
              <ArrowForwardOutlined />
            </Paper>
          );
        })
      )}
      {selectedClient !== null && (
        <>
          <PlexoCommerceText plexoCommerceName={selectedClient.plexoCommerceName} />
          <ClientSettings
            selectedClient={selectedClient!}
            handleBillingChange={handleBillingChange}
            createPaymentMethod={createPaymentMethod}
            updatePaymentMethod={updatePaymentMethod}
            beginDeletePaymentMethod={beginDeletePaymentMethod}
          />
          <ClientUsersTable selectedClient={selectedClient} isForDashboard={true} />
        </>
      )}
      <PaymentMethodDialog
        open={open}
        setOpen={setOpen}
        paymentMethod={selectedPaymentMethod}
        client={selectedClient ?? undefined}
      />
      <DeleteDialog
        open={deleteOpen}
        setOpen={setDeleteOpen}
        message={`¿Seguro que deseas eliminar el medio de pago ${paymentMethodToDelete?.issuerName}?`}
        deleteAction={confirmDeletePaymentMethod}
        deleteDisabled={deletingPaymentMethod}
      />
      <CustomSnackbar
        open={snackbarOpen}
        message={
          paymentMethods.deletePaymentMethodSuccess
            ? 'Se eliminó el medio de pago correctamente'
            : paymentMethods.deletePaymentMethodErrorMessage!
        }
        handleClose={closeSnack}
        type={paymentMethods.deletePaymentMethodSuccess ? Status.SUCCESS : Status.ERROR}
      />
    </div>
  );
}

export default ManageClients;
